/*
 * IMPORTS
 */
import React from 'react' // Npm: React.js library.
import PropTypes from 'prop-types' // Npm: Prop Types for checking the props type.
import Debounce from 'lodash/debounce' // Npm: Debounce library.
import AutoSizer from 'react-virtualized-auto-sizer' // Npm: React virtualized auto sizer.
import InfiniteLoader from 'react-window-infinite-loader' // Npm: React window infinite loader.
import NaturalCompare from 'natural-compare' // Npm: Sorting library.
import _ from 'underscore' // Npm: Underscore.js for utility functions.
import { BsCheckAll } from 'react-icons/bs' // Npm: React Icons for icons.
import { useLazyQuery, useQuery } from '@apollo/client' // Npm: Apollo client.
import { FixedSizeList as List } from 'react-window' // Npm: React Virtualized for virtualized list.
import { HiCheckCircle, HiPlusCircle, HiXCircle } from 'react-icons/hi2' // Npm: React Icons for icons.
import {
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react' // Npm: Chakra UI A simple, modular and accessible component library for React.


/*
 * COMPONENTS
 */
import { MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import VendorAccounttDirectoryReadQuery from './__query__/index.vendorAccounttDirectory.read.query'
import VendorAccounttReadQuery from './__query__/index.vendorAccountt.read.query'


/*
 * OBJECTS
 */
const Index = ({ vendorAccounttDirectoryValue, vendorAccounttValue, inValidVendorAccountt, disabled, inValidVendorAccounttDirectory, isRequired, onChange }) => {
  // Const assignment.
  const _skipDifference = 1000

  // Hook assignment.
  const [vendorAccounttDirectory, setVendorAccounttDirectory] = React.useState('')
  const [vendorAccountt, setVendorAccountt] = React.useState([])
  const [, setForceReRender] = React.useState(void 0)
  const [QueryVendorAccounttRead, QueryVendorAccounttReadResponse] = useLazyQuery(VendorAccounttReadQuery)
  const _vendorAccounttListRef = React.useRef([])
  const _oldVendorAccounttDirectory = React.useRef(void 0)
  const _QueryVendorAccounttDirectoryRead = useQuery(VendorAccounttDirectoryReadQuery, { 'variables': { 'take': _skipDifference, 'skip': 0 }, 'fetchPolicy': Object.React.App.fetchPolicy, 'pollInterval': Object.React.App.pollInterval })

  // Object assignment.
  const _LoadMore = async (__take, __skip, __replaceInstead) => {
    // Const assignment.
    const _vendorAccounttDirectoryId = (vendorAccounttDirectory)?.split('(')[1]?.split(')')[0]

    // If id is empty then just return.
    if (_.isEmpty(_vendorAccounttDirectoryId)) return void 0

    // Query for given directory read.
    const _QueryVendorAccounttRead = await QueryVendorAccounttRead({ 'variables': { 'take': __take, 'skip': __skip, 'vendorAccounttDirectoryId': _vendorAccounttDirectoryId } })

    // Report failure if caught exception.
    if (_QueryVendorAccounttRead?.error) throw _QueryVendorAccounttRead?.error

    // Update vendorAccountt list available.
    _vendorAccounttListRef.current = __replaceInstead ? _QueryVendorAccounttRead?.data?.VendorAccounttRead?.filter(r => 'READ_SUCCESSFUL' === r.status) : [..._vendorAccounttListRef.current, ...(_QueryVendorAccounttRead?.data?.VendorAccounttRead?.filter(r => 'READ_SUCCESSFUL' === r.status) ?? [])]

    // Return void.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    /*
     * Update selected country if vendorAccounttDirectory value
     * Is passed in params.
     */
    vendorAccounttDirectoryValue && _.isEmpty(vendorAccounttDirectory) && setVendorAccounttDirectory(vendorAccounttDirectoryValue ?? '')
    vendorAccounttValue && _.isEmpty(vendorAccountt) && setVendorAccountt(vendorAccounttValue ?? [])
  }, [vendorAccounttDirectoryValue, vendorAccounttValue])
  React.useEffect(() => {
    // Load more vendorAccountts.
    !_.isEmpty(vendorAccounttDirectory) && _LoadMore(10, 0).then(() => setForceReRender(Math.random()))
  }, [vendorAccounttDirectory])
  React.useEffect(() => {
    /*
     * If directory change occurs then refetch
     * new data.
     */
    if (_oldVendorAccounttDirectory.current !== vendorAccounttDirectory) {
      // Load new batch of data.
      _LoadMore(10, 0, true).then(() => setForceReRender(Math.random()))

      // Update old ref.
      _oldVendorAccounttDirectory.current = vendorAccounttDirectory
    }
  }, [vendorAccounttDirectory])

  // Component assignment.
  const _List = ({ index, style }) => _.isEmpty(_vendorAccounttListRef.current[index]) ? void 0 : (
    <MenuItem key={String.random(8)} style={style}>
      <Checkbox
        disabled={0 === _vendorAccounttListRef.current.length}
        name='vendorAccountt'
        onChange={() => {
          // Update state of vendorAccountts.
          setVendorAccountt(j => {
            /*
             * If only one selection is allowed
             * Then clear the array.
             */
            if (j.includes(`${_vendorAccounttListRef.current[index]?.displayName} (${_vendorAccounttListRef.current[index]?.id})`)) {
              // Const assignment.
              const _data = _.without(j, `${_vendorAccounttListRef.current[index]?.displayName} (${_vendorAccounttListRef.current[index]?.id})`, 'ALL')

              // Return updated mcc and mnc.
              onChange({ 'vendorAccountt': _data, 'vendorAccounttDirectory': vendorAccounttDirectory })

              // Return updated vendorAccountt.
              return _data
            }

            // Return updated vendorAccountt.
            onChange({ 'vendorAccountt': [...j, `${_vendorAccounttListRef.current[index]?.displayName} (${_vendorAccounttListRef.current[index]?.id})`], 'vendorAccounttDirectory': vendorAccounttDirectory })

            // Return updated vendorAccountt and id.
            return [...j, `${_vendorAccounttListRef.current[index]?.displayName} (${_vendorAccounttListRef.current[index]?.id})`]
          })
        }}
        isChecked={vendorAccountt.includes('ALL') || vendorAccountt.includes('ALL (ALL)') || vendorAccountt.includes(`${_vendorAccounttListRef.current[index]?.displayName} (${_vendorAccounttListRef.current[index]?.id})`)}>
        {`${_vendorAccounttListRef.current[index]?.displayName} (${_vendorAccounttListRef.current[index]?.id})`}
      </Checkbox>
    </MenuItem>
  )

  // Prop types.
  _List.propTypes = {
    'index': PropTypes.number,
    'style': PropTypes.object
  }

  // Return the JSX.
  return (
    <Flex w='100%' className='vendorAccounttDirectoryAndVendorAccounttSelector' flexDir='column' gap='22px'>
      <MemoizedSelect
        label='LCRV2 Folder'
        disabled={_QueryVendorAccounttDirectoryRead?.loading || disabled}
        name='vendorAccounttDirectory'
        value={vendorAccounttDirectory}
        placeholder='Select Folder'
        bg='gray.300'
        options={_.compact(_QueryVendorAccounttDirectoryRead?.data?.VendorAccounttDirectoryRead?.map?.(j => 'READ_SUCCESSFUL' === j.status ? `${j.displayName} (${j.id})` : void 0))?.sort((a, b) => NaturalCompare(a, b))}
        isInvalid={inValidVendorAccounttDirectory}
        onChange={__event => {
          // Update states.
          setVendorAccounttDirectory(__event.target.value)
          setVendorAccountt([])

          // Return updated vendorAccounttDirectory and vendorAccountt.
          onChange({ 'vendorAccountt': [], 'vendorAccounttDirectory': __event.target.value })
        }}
      />
      <FormControl flexDir='column' w='100%'>
        <FormLabel
          display='flex'
          alignItems='center'
          style={{ 'pointerEvents': 'none' }}>
          VendorAccount Selector{' '}{isRequired ? <Text color='red'>*</Text> : void 0}
        </FormLabel>
        <Menu closeOnSelect={false} position='relative' disabled={QueryVendorAccounttReadResponse.loading || _QueryVendorAccounttDirectoryRead?.loading || disabled} w='100%'>
          <MenuButton
            type='button'
            disabled={QueryVendorAccounttReadResponse.loading || _QueryVendorAccounttDirectoryRead?.loading || disabled}
            h='40px'
            color={disabled ? '#aaafc8' : void 0}
            px='4'
            onClick={e => e.stopPropagation()}
            m='0'
            w='100%'
            borderRadius='12px'
            transition='all 0.2s'
            textAlign='left'
            rightIcon={<BsCheckAll />}
            bg={inValidVendorAccountt ? 'rgb(255,255,255,0.8)' : 'gray.300'}
            _hover={{ 'bg': 'gray.300' }}
            _expanded={{ 'bg': 'gray.300' }}
            _focus={{ 'bg': 'gray.300' }}
            boxShadow={inValidVendorAccountt ? '0 0 0 1.5px #EE5D50' : void 0}>
            {vendorAccountt.includes('ALL') || vendorAccountt.includes('ALL (ALL)') ? _.first(_vendorAccounttListRef.current)?._totalCount : (_.isString(vendorAccountt) && !_.isEmpty(vendorAccountt) ? [vendorAccountt] : vendorAccountt)?.length ?? 0} Selected
          </MenuButton>
          <MenuList borderRadius={12} h='250px' w='100%'>
            <AutoSizer>
              {({ height, width }) => (
                <InfiniteLoader
                  width={width}
                  height={height}
                  isItemLoaded={__index => Boolean(_vendorAccounttListRef.current[__index])}
                  itemCount={_vendorAccounttListRef.current.length}
                  threshold={0.9}
                  loadMoreItems={(__startIndex, __endIndex) => _LoadMore(10, parseInt(__endIndex, 10) + 1)}>
                  {({ onItemsRendered, ref }) => (
                    <List
                      ref={ref}
                      height={height}
                      itemCount={_vendorAccounttListRef.current.length}
                      itemSize={52}
                      onItemsRendered={onItemsRendered}
                      overflow='hidden'
                      width={width}>
                      {_List}
                    </List>
                  )}
                </InfiniteLoader>
              )}
            </AutoSizer>
          </MenuList>
          <Flex position='absolute' right={0} bottom={0} zIndex={1000}>
            <IconButton
              aria-label='Select All'
              disabled={0 === _vendorAccounttListRef.current.length || QueryVendorAccounttReadResponse.loading || _QueryVendorAccounttDirectoryRead?.loading || disabled}
              _hover={{ 'bg': 'none' }}
              _active={{ 'bg': 'none' }}
              onClick={() => {
                // Const assignment.
                const _vendorAccounttIds = ['ALL']

                // Update vendorAccountt.
                setVendorAccountt(_vendorAccounttIds)

                // Return updated vendorAccountt.
                onChange({ 'vendorAccountt': _vendorAccounttIds, 'vendorAccounttDirectory': vendorAccounttDirectory })
              }}
              right='-10px'
              bg='none'
              p='0'>
              {vendorAccountt?.length === _vendorAccounttListRef.current?.length ? (<HiCheckCircle fontSize='20px' color='#c4c4c4' />) : (<HiPlusCircle fontSize='20px' color='#3CA55C' />)}
            </IconButton>
            <IconButton
              aria-label='Clear All'
              disabled={0 === _vendorAccounttListRef.current.length || QueryVendorAccounttReadResponse.loading || _QueryVendorAccounttDirectoryRead?.loading || disabled}
              _hover={{ 'bg': 'none' }}
              _active={{ 'bg': 'none' }}
              onClick={() => {
                // Update states.
                setVendorAccountt([])

                // Return updated mcc and mnc.
                onChange({ 'vendorAccountt': [], 'vendorAccounttDirectory': '' })
              }}
              bg='none'
              p='0'>
              {0 === vendorAccountt?.length ? (<HiXCircle fontSize='20px' color='#c4c4c4' />) : (<HiXCircle fontSize='20px' color='#FF416C' />)}
            </IconButton>
          </Flex>
        </Menu>
      </FormControl>
    </Flex>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'vendorAccounttDirectoryValue': PropTypes.string,
  'vendorAccounttIdValue': PropTypes.array,
  'vendorAccounttValue': PropTypes.array,
  'inValidVendorAccountt': PropTypes.bool,
  'disabled': PropTypes.bool,
  'inValidVendorAccounttDirectory': PropTypes.bool,
  'onChange': PropTypes.func,
  'isRequired': PropTypes.bool
}


/*
 * EXPORT
 */
export default Index
